import type { ExpectedStats, ImportResult, TenantAccess } from '@konyaxl/shared';
import { parseBarcodeList } from '@konyaxl/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QrDialog } from '../components/QrDialog.js';
import { ApiRequestError, api } from '../core/api.js';
import { panoyaKopyala } from '../core/pano.js';

interface Props {
  tenant: TenantAccess;
  onBack: () => void;
}

/**
 * Beklenen barkod listesi yönetimi — bilgisayardan yapılan iş.
 *
 * Kullanıcı o gün sahada okutulması gereken barkodları buraya yapıştırır.
 * Eski projede bu, telefonun panosundan okunuyor ve tek bir Firestore
 * dokümanına dizi olarak yazılıyordu (rabiekle.dart:216 "Barkod Listesi Ekle").
 */
export function ExpectedPage({ tenant, onBack }: Props) {
  const [metin, setMetin] = useState('');
  const [degistir, setDegistir] = useState(true);
  const [stats, setStats] = useState<ExpectedStats | null>(null);
  const [kalanlar, setKalanlar] = useState<string[]>([]);
  const [sonuc, setSonuc] = useState<ImportResult | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [qrKod, setQrKod] = useState<string | null>(null);
  const [kopyaNotu, setKopyaNotu] = useState<string | null>(null);
  const [kopyalaniyor, setKopyalaniyor] = useState(false);

  /**
   * Barkodlar alt alta kopyalanıyor.
   *
   * Listeye de alt alta yapıştırılıyorlar; aynı biçimde geri vermek Excel'e
   * sütun olarak yapışmasını sağlıyor. Tek satırda virgülle vermek burada
   * işe yaramazdı — liste yüzlerce koddan oluşuyor.
   */
  const kopyala = useCallback(async (kodlar: string[], etiket: string) => {
    if (kodlar.length === 0) return;
    const basarili = await panoyaKopyala(kodlar.join('\n'));
    setKopyaNotu(
      basarili ? `${kodlar.length} ${etiket} panoya kopyalandı` : 'Panoya kopyalanamadı',
    );
    setTimeout(() => setKopyaNotu(null), 2500);
  }, []);

  /** Tüm liste ayrıca indiriliyor: ekranda yalnızca okutulmayanlar var. */
  const tumunuKopyala = useCallback(async () => {
    setKopyalaniyor(true);
    try {
      const res = await api.get<{ items: string[] }>(
        `/${tenant.slug}/expected/all?limit=50000`,
      );
      await kopyala(res.items, 'barkod');
    } catch {
      setKopyaNotu('Liste alınamadı');
      setTimeout(() => setKopyaNotu(null), 2500);
    } finally {
      setKopyalaniyor(false);
    }
  }, [tenant.slug, kopyala]);

  /** Yapıştırılan metinde kaç kod olduğunu anında gösterir — sunucuyla aynı ayrıştırıcı. */
  const onizleme = useMemo(() => parseBarcodeList(metin), [metin]);

  async function yenile() {
    try {
      const [s, r] = await Promise.all([
        api.get<ExpectedStats>(`/${tenant.slug}/expected/stats`),
        api.get<{ items: string[] }>(`/${tenant.slug}/expected/remaining?limit=2000`),
      ]);
      setStats(s);
      setKalanlar(r.items);
    } catch (err) {
      setHata(err instanceof ApiRequestError ? err.message : 'Liste yüklenemedi');
    }
  }

  useEffect(() => {
    void yenile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.slug]);

  async function yukle() {
    if (onizleme.length === 0) return;
    setMesgul(true);
    setHata(null);
    setSonuc(null);

    try {
      const res = await api.post<ImportResult>(`/${tenant.slug}/expected/import`, {
        text: metin,
        replace: degistir,
      });
      setSonuc(res);
      setMetin('');
      await yenile();
    } catch (err) {
      setHata(err instanceof ApiRequestError ? err.message : 'Liste yüklenemedi');
    } finally {
      setMesgul(false);
    }
  }

  async function temizle() {
    if (!confirm('Beklenen barkod listesi tamamen silinecek. Emin misiniz?')) return;
    setMesgul(true);
    try {
      await api.del(`/${tenant.slug}/expected`);
      setSonuc(null);
      await yenile();
    } catch (err) {
      setHata(err instanceof ApiRequestError ? err.message : 'Liste silinemedi');
    } finally {
      setMesgul(false);
    }
  }

  return (
    <>
      <header className="ust-bar">
        <button className="btn-geri" onClick={onBack} aria-label="Geri">
          ‹
        </button>
        <h1>{tenant.name} — Beklenen barkodlar</h1>
        <div className="ust-bar-sag">
          {stats && stats.total > 0 && (
            <>
              <button
                className="btn-excel"
                onClick={() => void tumunuKopyala()}
                disabled={kopyalaniyor}
                title="Listedeki tüm barkodları panoya kopyala"
              >
                {kopyalaniyor ? 'Kopyalanıyor…' : `Tümünü kopyala (${stats.total})`}
              </button>
              <button className="btn-excel" onClick={() => void temizle()} disabled={mesgul}>
                Listeyi temizle
              </button>
            </>
          )}
        </div>
      </header>

      <main className="icerik">
        {stats && stats.total > 0 && (
          <div className="ozet-kartlar">
            <div className="ozet-kart">
              <span className="ozet-sayi">{stats.total}</span>
              <span className="ozet-etiket">Toplam</span>
            </div>
            <div className="ozet-kart yesil">
              <span className="ozet-sayi">{stats.scanned}</span>
              <span className="ozet-etiket">Okutuldu</span>
            </div>
            <div className="ozet-kart turuncu">
              <span className="ozet-sayi">{stats.remaining}</span>
              <span className="ozet-etiket">Kaldı</span>
            </div>
          </div>
        )}

        {hata && <div className="hata-kutu">{hata}</div>}
        {kopyaNotu && <div className="basari-kutu">{kopyaNotu}</div>}

        {sonuc && (
          <div className="basari-kutu">
            {sonuc.inserted} barkod eklendi
            {sonuc.duplicates > 0 && ` · ${sonuc.duplicates} tekrar atlandı`} · listede
            toplam {sonuc.total} barkod var
          </div>
        )}

        <section className="bolum">
          <h2 className="bolum-baslik">Liste yükle</h2>
          <p className="bolum-aciklama">
            Barkodları alt alta yapıştırın. Excel'den kopyalanan sütunlar da
            çalışır — satır, virgül, noktalı virgül ve sekme ayırıcı kabul edilir.
          </p>

          <textarea
            className="liste-alan"
            value={metin}
            onChange={(e) => setMetin(e.target.value)}
            placeholder={'6901234567890\n6901234567891\n6901234567892'}
            rows={10}
            spellCheck={false}
          />

          <div className="liste-arac">
            <span className="onizleme-bilgi">
              {onizleme.length > 0
                ? `${onizleme.length} geçerli barkod algılandı`
                : 'Henüz kod girilmedi'}
            </span>

            <label className="filtre-onay">
              <input
                type="checkbox"
                checked={degistir}
                onChange={(e) => setDegistir(e.target.checked)}
              />
              Mevcut listenin yerine yaz
            </label>

            <button
              className="btn-birincil dar"
              onClick={() => void yukle()}
              disabled={mesgul || onizleme.length === 0}
            >
              {mesgul ? 'Yükleniyor…' : 'Listeyi yükle'}
            </button>
          </div>
        </section>

        {kalanlar.length > 0 && (
          <section className="bolum">
            <h2 className="bolum-baslik">Okutulmayanlar ({kalanlar.length})</h2>
            <p className="bolum-aciklama">
              Koda tıklayın: panoya kopyalanır ve QR kodu açılır.
            </p>
            <div className="liste-arac">
              <span className="onizleme-bilgi">{kalanlar.length} okutulmayan barkod</span>
              <button
                className="btn-birincil dar"
                onClick={() => void kopyala(kalanlar, 'okutulmayan barkod')}
              >
                Okutulmayanları kopyala
              </button>
            </div>
            <div className="kalan-izgara">
              {kalanlar.map((kod) => (
                <button key={kod} className="kalan-etiket" onClick={() => setQrKod(kod)}>
                  {kod}
                </button>
              ))}
            </div>
          </section>
        )}
      </main>

      {qrKod && (
        <QrDialog
          kodlar={[{ etiket: 'Barkod', deger: qrKod }]}
          onClose={() => setQrKod(null)}
        />
      )}
    </>
  );
}
